// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { forwardRef, useId } from 'react';
import './Select.css';

export interface SelectOption {
  value: string;
  text: string;
}

interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  error?: string;
  helperText?: string;
  fluid?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      placeholder,
      options,
      error,
      helperText,
      required,
      disabled,
      fluid = false,
      className = '',
      value,
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = idProp ?? autoId;
    const isFilled = value !== undefined && value !== '';

    const wrapperClasses = [
      'fin-select',
      error && 'fin-select--error',
      disabled && 'fin-select--disabled',
      isFilled && 'fin-select--filled',
      fluid && 'fin-select--fluid',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        {label && (
          <label className="fin-select__label" htmlFor={id}>
            {label}
            {required && <span className="fin-select__required">*</span>}
          </label>
        )}
        <div className="fin-select__wrapper">
          <select
            ref={ref}
            id={id}
            className="fin-select__field"
            disabled={disabled}
            required={required}
            value={value}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${id}-error` : helperText ? `${id}-helper` : undefined
            }
            {...rest}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.text}
              </option>
            ))}
          </select>
          <span className="fin-select__chevron" aria-hidden="true">
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
        {error && (
          <p className="fin-select__error" id={`${id}-error`} role="alert">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p className="fin-select__helper" id={`${id}-helper`}>
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
